import { Plus } from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useCreatorTags } from "@/api/creator-tags";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CreatorTag } from "@/pages/creator-tags/tag";
import { TagPickerPopover } from "./tag-picker-popover";

type CreatorTagsCellProps = {
  tiktokCreatorOpenId: string | null;
  /** IDs of the tags assigned to this creator; names/colours come from the live tag list. */
  tagIds: string[];
  /** Opens the Tag Library modal (owned by the page) from the "Manage All Tags" link. */
  onManageAllTags: () => void;
  /** How many pills to show before collapsing the rest into a "+N" overflow. */
  maxVisible?: number;
};

// Bare content, no `<Table.Cell>` wrapper — for callers (like UnboxTable columns)
// that supply their own cell wrapper.
export const CreatorTagsCellContent = ({
  tiktokCreatorOpenId,
  tagIds,
  onManageAllTags,
  maxVisible = 3,
}: CreatorTagsCellProps) => {
  const { data: allTags = [], isLoading } = useCreatorTags();
  // Staged ids while the picker is open, so selections preview inline before the
  // diff is committed on close. `null` means "use the server ids".
  const [previewIds, setPreviewIds] = useState<string[] | null>(null);
  const effectiveIds = previewIds ?? tagIds;

  // Resolve assigned IDs to live tags (preserving assignment order) so name/colour
  // edits in the Tag Library reflect here without refetching the row.
  const tags = useMemo(() => {
    const byId = new Map(allTags.map((tag) => [tag.id, tag]));
    return effectiveIds.map((id) => byId.get(id)).filter((tag) => tag !== undefined);
  }, [allTags, effectiveIds]);

  // While the tag list loads we can't resolve names/colours yet — show a
  // placeholder for creators that have assignments, rather than misleadingly
  // flashing an empty "+ Add Tag" cell. Untagged creators need no wait.
  if (isLoading && tagIds.length > 0) {
    return (
      <div className="flex flex-row items-center gap-1" aria-hidden>
        <span className="h-5 w-16 animate-pulse rounded-full bg-secondary" />
        {tagIds.length > 1 && <span className="h-5 w-10 animate-pulse rounded-full bg-secondary" />}
      </div>
    );
  }

  if (!tiktokCreatorOpenId) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-dashed border-secondary px-2.5 py-0.5 text-xs font-medium text-disabled">
              <Plus className="size-3.5" />
              Add Tag
            </span>
          </TooltipTrigger>
          <TooltipContent align="start" className="max-w-[260px]">
            This creator can't be tagged because we couldn't match them to their TikTok profile.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const visible = tags.slice(0, maxVisible);
  const overflow = tags.slice(maxVisible);
  const isEmpty = tags.length === 0;

  return (
    // The whole cell is the picker trigger so it's hard to mis-click onto the
    // creator row. Must be a real <button>: react-aria-components excludes
    // native interactive elements from the row's onRowAction (a div[role=button]
    // would still open the creator drawer). stopPropagation guards selection.
    <TagPickerPopover
      tiktokCreatorOpenId={tiktokCreatorOpenId}
      assignedTagIds={tagIds}
      onManageAllTags={onManageAllTags}
      onStagedChange={setPreviewIds}
    >
      <button
        type="button"
        aria-label={isEmpty ? "Add tag" : "Edit tags"}
        onClick={(e) => e.stopPropagation()}
        className="flex min-h-7 w-full cursor-pointer flex-row flex-wrap items-center gap-1 rounded-md px-1 py-0.5 text-left transition-colors hover:bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
      >
        {isEmpty ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-dashed border-secondary px-2.5 py-0.5 text-xs font-medium text-tertiary">
            <Plus className="size-3.5" />
            Add Tag
          </span>
        ) : (
          <>
            {visible.map((tag) => (
              <CreatorTag key={tag.id} tag={tag} />
            ))}
            {/* Overflow chip: hover reveals the hidden tag names. A Tooltip (span
                trigger) rather than a HoverCard (button trigger) so it stays valid
                nested inside the trigger button. */}
            {overflow.length > 0 && (
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center rounded-full border border-secondary px-2 py-0.5 text-xs font-medium text-tertiary">
                      +{overflow.length}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent align="start" className="flex max-w-[260px] flex-col items-start gap-0.5">
                    {overflow.map((tag) => (
                      <span key={tag.id}>{tag.name}</span>
                    ))}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Compact "+" affordance hints the cell is editable. */}
            <span className="inline-flex size-5 items-center justify-center rounded-full border border-dashed border-secondary text-tertiary">
              <Plus className="size-3.5" />
            </span>
          </>
        )}
      </button>
    </TagPickerPopover>
  );
};
